import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, Platform, ActivityIndicator, Image, TouchableOpacity, Animated, Easing } from 'react-native';
import PropTypes from 'prop-types';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { API_HOST } from '@/constants/config';
import { htmlScriptValue } from '@/lib/macArtifactNavigation';

const isWeb = Platform.OS === 'web';

// Platforms where we know the viewer pipeline works end-to-end:
//   - web     → <iframe srcDoc> (three.js path) + native <model-viewer> element
//   - ios     → react-native-webview (incl. iOS-specific Quick Look AR via ios-src)
//   - android → react-native-webview + scene-viewer AR fallback inside model-viewer
//   - macos   → react-native-macos exposes react-native-webview; same path as ios
// Everything else (windows, tvOS, web workers, etc.) gets gracefully degraded
// to the thumbnail + download CTA so we never render a broken viewer.
const SUPPORTED_VIEWER_PLATFORMS = new Set(['web', 'ios', 'android', 'macos']);
const isPlatformSupported = SUPPORTED_VIEWER_PLATFORMS.has(Platform.OS);

// Bump this whenever you upgrade @google/model-viewer in backend/package.json.
// The version is appended as a `?v=` cache-buster on every URL so Cloudflare
// (which caches the route for 4h via cache-control: max-age=14400) can't
// keep serving a stale 404 from before the file was installed, or a stale
// older version after an upgrade.
const MODEL_VIEWER_VERSION = '4.2.0';
const THREE_VERSION = '0.182.0';
// Self-hosted from our own backend (backend/server.js mounts these from
// node_modules at /assets2/tp/...). We deliberately do NOT load Google's
// model-viewer or three.js from third-party CDNs (ajax.googleapis.com,
// unpkg.com, etc.) — every script the site executes must come from our
// own origin. Bump the npm pins in backend/package.json to upgrade.
export function getModel3DViewerOrigins(platform, apiHost) {
  const apiOrigin = (apiHost || '').replace(/\/+$/, '');
  // Saved models allow the production app origin through CDN CORS. A native
  // preview must keep that origin even when the API uses a beta tunnel.
  const documentOrigin = platform === 'macos' ? 'https://gab.ai' : apiOrigin || 'https://gab.ai';
  // An HTTPS document cannot import modules from a local HTTP dev server.
  // Keep HTTPS beta modules usable; use our shipping assets for HTTP builds.
  const rendererOrigin = platform === 'macos' && !apiOrigin.startsWith('https://')
    ? 'https://gab.ai'
    : apiOrigin;
  return { rendererOrigin, baseUrl: `${documentOrigin}/3d-viewer/` };
}
const { rendererOrigin: SELF_HOSTED_ORIGIN, baseUrl: VIEWER_BASE_URL } = getModel3DViewerOrigins(Platform.OS, API_HOST);
const MODEL_VIEWER_CDN = `${SELF_HOSTED_ORIGIN}/assets2/tp/model-viewer.min.js?v=${MODEL_VIEWER_VERSION}`;
const THREE_MODULE_URL = `${SELF_HOSTED_ORIGIN}/assets2/tp/three/build/three.module.js?v=${THREE_VERSION}`;
// Importmap prefix — cannot carry a `?v=` query string because the
// browser appends sub-paths *after* it (e.g. `loaders/OBJLoader.js`),
// which would produce malformed URLs. If Cloudflare ends up with a
// cached 404 for any of the addon files, purge the path explicitly
// from your Cloudflare dashboard or wait out the 4h max-age.
const THREE_ADDONS_URL = `${SELF_HOSTED_ORIGIN}/assets2/tp/three/examples/jsm/`;

// Lazy + defensive WebView resolution. On platforms where react-native-webview
// isn't installed (or fails to register a native module — e.g. older RN-macOS
// builds, or any future platform), `require` may either throw at load time or
// hand back a module whose `WebView` blows up on mount. Hold the result so we
// only pay the resolution cost once and can fall back cleanly.
let webViewResolved = false;
let resolvedWebView = null;
function resolveWebView() {
  if (webViewResolved) return resolvedWebView;
  webViewResolved = true;
  if (isWeb) return null; // never need react-native-webview on web
  try {
    const mod = require('react-native-webview');
    resolvedWebView = Platform.OS === 'macos' ? require('@/components/Files/MacArtifactWebView').default : mod?.WebView || null;
  } catch (_) {
    resolvedWebView = null;
  }
  return resolvedWebView;
}
const isWebViewAvailable = () => isWeb || !!resolveWebView();

export function buildViewerHtml({ modelUrl, posterUrl, iosSrc, autoRotate, backgroundColor }) {
  const safeModel = htmlScriptValue(modelUrl || '');
  const safePoster = htmlScriptValue(posterUrl || '');
  const safeIosSrc = htmlScriptValue(iosSrc || '');
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
    <style>
      html, body { margin: 0; padding: 0; width: 100%; height: 100%; background: ${backgroundColor}; overflow: hidden; }
      model-viewer { width: 100% !important; height: 100% !important; min-height: 100%; display: block; --poster-color: ${backgroundColor}; }
      #fallback { display: none; color: #fff; font-family: -apple-system, system-ui, sans-serif; padding: 16px; text-align: center; }
      #fallback.show { display: block; }
    </style>
    <script type="module" src="${MODEL_VIEWER_CDN}" crossorigin="anonymous"></script>
  </head>
  <body>
    <model-viewer
      id="viewer"
      src=""
      ${autoRotate ? 'auto-rotate auto-rotate-delay="0" rotation-per-second="20deg"' : ''}
      camera-controls
      touch-action="pan-y"
      interaction-prompt="none"
      shadow-intensity="0.6"
      exposure="1"
      environment-image="neutral"
      ar
      ar-modes="webxr scene-viewer quick-look"
      reveal="auto"
    ></model-viewer>
    <div id="fallback">Failed to load 3D model.</div>
    <script>
      (function() {
        var viewer = document.getElementById('viewer');
        var fallback = document.getElementById('fallback');
        var modelUrl = ${safeModel};
        var posterUrl = ${safePoster};
        var iosSrc = ${safeIosSrc};
        function postToHost(type, message) {
          var payload = JSON.stringify({ source: 'gab-3d-viewer', type: type, message: message || '' });
          try { window.parent && window.parent !== window && window.parent.postMessage(payload, '*'); } catch (e) {}
          try { window.ReactNativeWebView && window.ReactNativeWebView.postMessage(payload); } catch (e) {}
        }

        var readySent = false;
        function sendReady() {
          if (readySent) return;
          readySent = true;
          postToHost('ready');
        }

        function applySrc(url) {
          if (!url) return;
          viewer.setAttribute('src', url);
          try { viewer.src = url; } catch (e) {}
        }

        function loadAsBlob(url) {
          fetch(url).then(function(res) {
            if (!res.ok) throw new Error(String(res.status));
            return res.blob();
          }).then(function(blob) {
            var typed = blob.type && /gltf|glb|model/i.test(blob.type)
              ? blob
              : new Blob([blob], { type: 'model/gltf-binary' });
            applySrc(URL.createObjectURL(typed));
          }).catch(function() {});
        }

        function start() {
          viewer = document.getElementById('viewer');
          if (!viewer) return;
          viewer.style.width = '100%';
          viewer.style.height = '100%';
          viewer.style.display = 'block';
          if (posterUrl) viewer.setAttribute('poster', posterUrl);
          if (iosSrc) viewer.setAttribute('ios-src', iosSrc);
          if (modelUrl) applySrc(modelUrl);

          // Only the model itself failing to load is fatal. Other error
          // events (env-image misses, AR availability, transient webgl
          // hiccups) should be ignored so we don't blank the viewer over
          // a successfully-rendered scene.
          viewer.addEventListener('error', function(e) {
            var type = e && e.detail && e.detail.type;
            if (type && type !== 'loadfailure') return;
            if (modelUrl && viewer.getAttribute('src') === modelUrl) {
              loadAsBlob(modelUrl);
              return;
            }
            fallback.classList.add('show');
            postToHost('error', type || 'load failed');
          });
          viewer.addEventListener('load', sendReady);
          viewer.addEventListener('model-visibility', function(e) {
            if (e && e.detail && e.detail.visible) sendReady();
          });
          // CDN GLBs sometimes never fire load under CORS. Retry as a
          // same-origin blob, then treat a painted canvas as ready.
          setTimeout(function() {
            if (!readySent && modelUrl && viewer.getAttribute('src') === modelUrl) loadAsBlob(modelUrl);
          }, 2500);
          var painted = setInterval(function() {
            if (readySent) { clearInterval(painted); return; }
            var canvas = viewer.shadowRoot && viewer.shadowRoot.querySelector('canvas');
            if (canvas && canvas.width > 300 && window.getComputedStyle(canvas).display !== 'none') {
              sendReady();
              clearInterval(painted);
            }
          }, 300);
        }
        if (window.customElements && customElements.whenDefined) {
          customElements.whenDefined('model-viewer').then(start);
        } else {
          start();
        }
      })();
    </script>
  </body>
</html>`;
}

function getInferredFormat(modelUrl) {
  if (!modelUrl) return null;
  try {
    const cleaned = modelUrl.split('?')[0].split('#')[0];
    const ext = cleaned.split('.').pop()?.toLowerCase();
    if (!ext) return null;
    if (['glb', 'gltf', 'obj', 'fbx', 'usdz', 'ply', 'stl'].includes(ext)) return ext;
  } catch {
    /* noop */
  }
  return null;
}

// model-viewer (Google's web component) only natively renders glTF / GLB.
const SUPPORTED_BY_MODEL_VIEWER = new Set(['glb', 'gltf']);
// Formats we render via a hand-rolled three.js scene (CDN-loaded, no npm dep).
// OBJ is the big one — Hunyuan Rapid + plenty of legacy assets ship .obj as the
// primary mesh, and "Preview unavailable" for those is a terrible UX.
const SUPPORTED_BY_THREE = new Set(['obj', 'stl', 'ply', 'glb', 'gltf']);
const SUPPORTED_FORMATS = new Set([...SUPPORTED_BY_MODEL_VIEWER, ...SUPPORTED_BY_THREE]);

/**
 * True if the current OS has a working 3D renderer pipeline at all (regardless
 * of file format). Use this to hide 3D-only UI affordances entirely on
 * unsupported platforms — e.g. don't list 3D models in the chat input model
 * selector on RN-windows.
 */
export function isModel3DSupportedOnPlatform() {
  if (!isPlatformSupported) return false;
  if (!isWeb && !isWebViewAvailable()) return false;
  return true;
}

/**
 * True if we can render `(modelUrl, alternateFormats)` in-app — i.e. either
 * the primary asset OR one of its alternates is a format we know how to draw,
 * AND the current platform actually has a renderer available (web has the
 * iframe / model-viewer paths; native needs react-native-webview installed).
 *
 * Card components use this to decide whether to show a "tap to view" overlay
 * vs. fall straight back to the thumbnail with a download CTA, instead of
 * letting the user activate into a dead "Preview unavailable" placeholder.
 *
 * Exported so Model3DCardContent (and any other host) can stay in sync with
 * the viewer's actual format + platform coverage without re-encoding either.
 */
export function isModel3DRenderable(modelUrl, alternateFormats) {
  if (!isModel3DSupportedOnPlatform()) return false;
  const primary = getInferredFormat(modelUrl);
  if (primary && SUPPORTED_FORMATS.has(primary)) return true;
  if (Array.isArray(alternateFormats)) {
    for (const f of alternateFormats) {
      const fmt = (f?.format || '').toLowerCase();
      if (SUPPORTED_FORMATS.has(fmt)) return true;
      const altInferred = getInferredFormat(f?.url);
      if (altInferred && SUPPORTED_FORMATS.has(altInferred)) return true;
    }
  }
  return false;
}

// Pick a sibling MTL out of the alternateFormats payload so OBJLoader can
// at least apply named materials (color, shininess) — note this won't pull
// any image maps the MTL references because providers like Hunyuan ship
// those at unrelated CDN paths that don't resolve relative to the .mtl file.
function findMtlUrl(alternateFormats) {
  if (!Array.isArray(alternateFormats)) return null;
  const mtl = alternateFormats.find((f) => {
    const fmt = (f?.format || '').toLowerCase();
    if (fmt === 'mtl') return true;
    const url = (f?.url || '').toLowerCase().split('?')[0];
    return url.endsWith('.mtl');
  });
  return mtl?.url || null;
}

// Pick the diffuse / base-color texture image. Hunyuan exposes it under
// `model_urls.texture` (→ `format: 'texture'`); Meshy exposes per-PBR-channel
// textures under `texture_urls[].{baseColor, metallic, roughness, normal}`
// (→ `format: 'texture_baseColor'` etc.). We only need the base color for
// straightforward shading on OBJ — additional PBR maps would need a deeper
// material rebuild that's not worth the complexity for the in-app preview.
function findTextureUrl(alternateFormats) {
  if (!Array.isArray(alternateFormats)) return null;
  const baseColor = alternateFormats.find((f) => {
    const fmt = (f?.format || '').toLowerCase();
    return fmt === 'texture_basecolor' || fmt.startsWith('texture_basecolor_');
  });
  if (baseColor?.url) return baseColor.url;
  const named = alternateFormats.find((f) => (f?.format || '').toLowerCase() === 'texture');
  if (named?.url) return named.url;
  // Last-ditch fallback: any image alternate (skip MTL/OBJ/etc.).
  const img = alternateFormats.find((f) => {
    const mime = (f?.mimeType || '').toLowerCase();
    if (mime.startsWith('image/')) return true;
    const url = (f?.url || '').toLowerCase().split('?')[0];
    return /\.(png|jpe?g|webp)$/.test(url);
  });
  return img?.url || null;
}

export function buildThreeViewerHtml({
  modelUrl,
  mtlUrl,
  textureUrl,
  format,
  autoRotate,
  backgroundColor,
}) {
  const safeModel = htmlScriptValue(modelUrl || '');
  const safeMtl = htmlScriptValue(mtlUrl || '');
  const safeTexture = htmlScriptValue(textureUrl || '');
  const safeFormat = htmlScriptValue((format || '').toLowerCase());
  const safeBg = htmlScriptValue(backgroundColor || '#1a1a1a');
  const safeAutoRotate = htmlScriptValue(!!autoRotate);
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover, maximum-scale=1, user-scalable=no" />
    <style>
      html, body { margin: 0; padding: 0; width: 100%; height: 100%; background: ${backgroundColor}; overflow: hidden; }
      canvas { display: block; touch-action: none; outline: none; }
      #fallback { position: absolute; inset: 0; display: none; align-items: center; justify-content: center; color: rgba(255,255,255,0.8); font: 13px -apple-system, system-ui, sans-serif; padding: 16px; text-align: center; }
      #fallback.show { display: flex; }
      #spinner { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
      #spinner .ring { width: 28px; height: 28px; border: 2px solid rgba(255,255,255,0.2); border-top-color: rgba(255,255,255,0.8); border-radius: 50%; animation: spin 0.9s linear infinite; }
      @keyframes spin { to { transform: rotate(360deg); } }
    </style>
    <script type="importmap">
      {
        "imports": {
          "three": "${THREE_MODULE_URL}",
          "three/addons/": "${THREE_ADDONS_URL}"
        }
      }
    </script>
  </head>
  <body>
    <div id="spinner"><div class="ring"></div></div>
    <div id="fallback">Failed to load 3D model.</div>
    <script type="module">
      (async function() {
      const spinner = document.getElementById('spinner');
      const fallback = document.getElementById('fallback');
      function notifyHost(type, message) {
        const payload = JSON.stringify({ source: 'gab-3d-viewer', type, message: message || '' });
        try { window.parent && window.parent !== window && window.parent.postMessage(payload, '*'); } catch (_) {}
        try { window.ReactNativeWebView && window.ReactNativeWebView.postMessage(payload); } catch (_) {}
      }
      function showError(error) {
        spinner.style.display = 'none';
        fallback.classList.add('show');
        notifyHost('error', (error && error.message) || 'load failed');
      }
      try {
      // Dynamic imports let an unavailable renderer reach the same visible
      // failure state as a missing model, including in WKWebView.
      const [THREE, { OrbitControls }, { OBJLoader }, { MTLLoader }, { STLLoader }, { PLYLoader }, { GLTFLoader }] = await Promise.all([
        import('three'), import('three/addons/controls/OrbitControls.js'),
        import('three/addons/loaders/OBJLoader.js'), import('three/addons/loaders/MTLLoader.js'),
        import('three/addons/loaders/STLLoader.js'), import('three/addons/loaders/PLYLoader.js'),
        import('three/addons/loaders/GLTFLoader.js'),
      ]);

      const MODEL_URL = ${safeModel};
      const MTL_URL = ${safeMtl};
      const TEXTURE_URL = ${safeTexture};
      const FORMAT = ${safeFormat};
      const AUTO_ROTATE = ${safeAutoRotate};
      const BG = ${safeBg};

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: false });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      // setSize(w, h, true) sets BOTH the drawing buffer (w * pixelRatio) AND
      // the canvas CSS size (w x h). Passing false (the prior code) skipped
      // the CSS step, which on retina displays left the canvas intrinsic size
      // at 2x the iframe — overflowing it and clipping everything except the
      // top-left quadrant. That is why the OBJ/STL/PLY model looked off-center
      // while the GLB model-viewer path (which manages its own canvas sizing)
      // was fine. Always update style here.
      renderer.setSize(window.innerWidth, window.innerHeight, true);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      document.body.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(BG);

      const camera = new THREE.PerspectiveCamera(40, window.innerWidth / Math.max(window.innerHeight, 1), 0.01, 5000);
      camera.position.set(0, 0, 3);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.enablePan = false;
      controls.minDistance = 0.1;
      controls.maxDistance = 1000;
      // Track whether the user has manually orbited/zoomed so we know whether
      // it's safe to re-frame on resize (we only auto-frame until the user
      // touches it, then we never yank their view back).
      let userInteracted = false;
      controls.addEventListener('start', () => { userInteracted = true; });

      // Soft three-point-ish lighting so untextured OBJs still look like geometry,
      // not a flat silhouette.
      scene.add(new THREE.HemisphereLight(0xffffff, 0x303030, 1.1));
      const key = new THREE.DirectionalLight(0xffffff, 1.1);
      key.position.set(5, 8, 6);
      scene.add(key);
      const fill = new THREE.DirectionalLight(0xffffff, 0.45);
      fill.position.set(-6, 2, -4);
      scene.add(fill);

      let modelObject = null;
      let modelPivot = null;
      let modelRadius = 0;

      // Offset a wrapper once, before orbiting. World-space centers cannot be
      // subtracted from each mesh's local vertices: scaled/rotated parts and
      // shared geometry would move by different amounts or be changed twice.
      function recenterAndMeasure(obj) {
        obj.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(obj, true);
        if (box.isEmpty()) throw new Error('The model contains no visible geometry.');
        const center = box.getCenter(new THREE.Vector3());
        const centered = new THREE.Group();
        centered.position.copy(center).negate();
        centered.add(obj);
        return { object: centered, radius: Math.max(box.getBoundingSphere(new THREE.Sphere()).radius, 0.001) };
      }

      // Camera-only fit. Safe to call any time, including while modelPivot is
      // mid-rotation, because it never touches the model's transform.
      function fitCamera(radius) {
        if (!radius) return;
        const fovV = camera.fov * (Math.PI / 180);
        const aspect = Math.max(camera.aspect, 0.0001);
        // Horizontal FOV is the constraining one in portrait viewports
        // (fovH < fovV when aspect < 1). Pull the camera back to whichever
        // axis is tighter so the model never gets clipped on the sides.
        const fovH = 2 * Math.atan(Math.tan(fovV / 2) * aspect);
        const fitFov = Math.min(fovV, fovH);
        const dist = (radius / Math.sin(fitFov / 2)) * 1.25;

        // Slight 3/4 view (~15° above horizon) — matches Google
        // <model-viewer>'s default camera-orbit "0deg 75deg auto", so the
        // OBJ/STL/PLY viewer feels visually consistent with the GLB one.
        const polar = (15 * Math.PI) / 180;
        camera.position.set(0, dist * Math.sin(polar), dist * Math.cos(polar));
        camera.lookAt(0, 0, 0);
        camera.near = Math.max(radius * 0.01, 0.001);
        camera.far = Math.max(dist * 10, radius * 100);
        camera.updateProjectionMatrix();
        controls.target.set(0, 0, 0);
        controls.minDistance = dist * 0.1;
        controls.maxDistance = dist * 10;
        controls.update();
      }

      function ensureMaterial(obj) {
        // OBJ files without an MTL come through with a null/empty material; give
        // them a sensible default so the geometry actually shows up shaded.
        const fallbackMat = new THREE.MeshStandardMaterial({ color: 0xc8c8c8, roughness: 0.75, metalness: 0.05 });
        obj.traverse((child) => {
          if (!child.isMesh) return;
          const mat = child.material;
          const empty = !mat || (Array.isArray(mat) && mat.length === 0);
          const blank = mat && !Array.isArray(mat) && mat.isMeshBasicMaterial && !mat.map;
          if (empty || blank) child.material = fallbackMat;
          if (child.geometry && !child.geometry.attributes.normal) {
            child.geometry.computeVertexNormals();
          }
        });
      }

      // Try to load the diffuse texture in parallel with the OBJ — most providers
      // ship the base color image at a sibling URL (Hunyuan: model_urls.texture)
      // rather than embedding it in the MTL, so MTL alone leaves the model gray.
      function loadTextureMaybe() {
        if (!TEXTURE_URL) return Promise.resolve(null);
        return new Promise((resolve) => {
          const loader = new THREE.TextureLoader();
          loader.setCrossOrigin('anonymous');
          loader.load(
            TEXTURE_URL,
            (tex) => {
              tex.colorSpace = THREE.SRGBColorSpace;
              // OBJ UVs follow the classic OpenGL convention (origin bottom-left),
              // which matches three's default flipY=true — leave it alone.
              tex.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy?.() || 1);
              resolve(tex);
            },
            undefined,
            (err) => {
              console.warn('Texture load failed, falling back to untextured material', err);
              resolve(null);
            },
          );
        });
      }

      function applyTextureToMeshes(obj, texture) {
        // Replace each mesh's material with a textured StandardMaterial. We
        // build fresh per-mesh so we can safely mutate without leaking state
        // back into shared MTL materials.
        obj.traverse((child) => {
          if (!child.isMesh) return;
          const make = () => {
            const mat = new THREE.MeshStandardMaterial({
              map: texture,
              color: 0xffffff, // don't tint the texture
              roughness: 0.85,
              metalness: 0.05,
            });
            mat.needsUpdate = true;
            return mat;
          };
          if (Array.isArray(child.material)) {
            child.material = child.material.map(make);
          } else {
            child.material = make();
          }
          if (child.geometry && !child.geometry.attributes.normal) {
            child.geometry.computeVertexNormals();
          }
        });
      }

      async function loadObj() {
        const objLoader = new OBJLoader();
        // Only chain MTL when we don't have an explicit texture URL — the MTL
        // would otherwise overwrite materials that we'd then immediately
        // overwrite again with the textured StandardMaterial.
        if (MTL_URL && !TEXTURE_URL) {
          try {
            const mtlLoader = new MTLLoader();
            mtlLoader.setCrossOrigin('anonymous');
            const mtl = await mtlLoader.loadAsync(MTL_URL);
            mtl.preload();
            objLoader.setMaterials(mtl);
          } catch (err) {
            console.warn('MTL load failed, falling back to default material', err);
          }
        }
        const [obj, texture] = await Promise.all([
          objLoader.loadAsync(MODEL_URL),
          loadTextureMaybe(),
        ]);
        if (texture) {
          applyTextureToMeshes(obj, texture);
        } else {
          ensureMaterial(obj);
        }
        return obj;
      }

      async function loadStl() {
        const geometry = await new STLLoader().loadAsync(MODEL_URL);
        geometry.computeVertexNormals();
        const material = new THREE.MeshStandardMaterial({ color: 0xc8c8c8, roughness: 0.7, metalness: 0.1 });
        return new THREE.Mesh(geometry, material);
      }

      async function loadPly() {
        const geometry = await new PLYLoader().loadAsync(MODEL_URL);
        if (!geometry.attributes.normal) geometry.computeVertexNormals();
        const hasColors = !!geometry.attributes.color;
        const material = hasColors
          ? new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.75, metalness: 0.05 })
          : new THREE.MeshStandardMaterial({ color: 0xc8c8c8, roughness: 0.75, metalness: 0.05 });
        return new THREE.Mesh(geometry, material);
      }

      async function loadGltf() {
        // Generated GLBs can contain Draco-compressed geometry. GLTFLoader
        // requires an explicit decoder; serve it beside our existing modules.
        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
        const draco = new DRACOLoader();
        draco.setDecoderPath(${htmlScriptValue(`${THREE_ADDONS_URL}libs/draco/gltf/`)});
        draco.setWorkerLimit(1);
        try {
          const gltf = await new GLTFLoader().setDRACOLoader(draco).loadAsync(MODEL_URL);
          return gltf.scene || gltf.scenes[0];
        } finally {
          // Geometry is decoded by this point; keep no decoder worker alive.
          draco.dispose();
        }
      }

      async function load() {
        try {
          let obj;
          if (FORMAT === 'obj') obj = await loadObj();
          else if (FORMAT === 'stl') obj = await loadStl();
          else if (FORMAT === 'ply') obj = await loadPly();
          else if (FORMAT === 'glb' || FORMAT === 'gltf') obj = await loadGltf();
          else throw new Error('Unsupported format: ' + FORMAT);

          modelObject = obj;
          const centered = recenterAndMeasure(obj);
          modelRadius = centered.radius;
          // Orbit around the centered wrapper without modifying the model.
          modelPivot = new THREE.Group();
          modelPivot.add(centered.object);
          scene.add(modelPivot);
          // Step 3 position the camera. Pure camera math, doesn't touch the model.
          fitCamera(modelRadius);
          spinner.style.display = 'none';
          notifyHost('ready');
        } catch (err) {
          console.error('3D load failed', err);
          showError(err);
        }
      }

      load();

      function tick() {
        requestAnimationFrame(tick);
        if (AUTO_ROTATE && modelPivot) modelPivot.rotation.y += 0.005;
        controls.update();
        renderer.render(scene, camera);
      }
      tick();

      function syncSize() {
        const w = window.innerWidth;
        const h = Math.max(window.innerHeight, 1);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        // updateStyle=true keeps the canvas CSS size matched to the iframe;
        // see the WebGLRenderer setup above for why this matters on retina.
        renderer.setSize(w, h, true);
        // Recompute camera distance for the new aspect, but NEVER re-measure
        // or re-translate the model — that's already permanently centered in
        // recenterAndMeasure(). Re-measuring after rotation has started would
        // produce a rotation-dependent AABB center and walk the model off-screen.
        if (!userInteracted && modelRadius) fitCamera(modelRadius);
      }
      window.addEventListener('resize', syncSize);
      // ResizeObserver catches parent-driven layout changes that don't fire
      // a window 'resize' event (very common inside react-native-webview and
      // CSS-sized iframes).
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => syncSize());
        ro.observe(document.documentElement);
      }
      } catch (error) { showError(error); }
      })();
    </script>
  </body>
</html>`;
}

// ─── Loading overlay ──────────────────────────────────────────────────────
// 3D models — especially OBJ/GLB scenes from Tripo/Hunyuan/Meshy — can take
// several seconds to download AND parse before the first frame draws. The
// previous loading state (a tiny ActivityIndicator) gave users no signal
// about how long this might take, which felt broken. The overlay below sets
// expectations explicitly while masking the 1–5s gap in a way that feels
// considered, not stuck.
const LoadingOverlay = React.memo(({ posterUrl, backgroundColor, colors, isDark }) => {
  // Two interleaved animations:
  //   - rotation: a slow 4s linear spin that gives the cube its "loading" affordance
  //   - pulse: a 1.6s ease-in-out scale + opacity breath, so the cube feels alive
  //            even at the boundary between rotations (when motion otherwise
  //            looks flat to the eye).
  const rotation = useRef(new Animated.Value(0)).current;
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const spin = Animated.loop(
      Animated.timing(rotation, {
        toValue: 1,
        duration: 4000,
        easing: Easing.linear,
        useNativeDriver: !isWeb,
      }),
    );
    const breathe = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 800, easing: Easing.inOut(Easing.ease), useNativeDriver: !isWeb }),
        Animated.timing(pulse, { toValue: 0, duration: 800, easing: Easing.inOut(Easing.ease), useNativeDriver: !isWeb }),
      ]),
    );
    spin.start();
    breathe.start();
    return () => {
      spin.stop();
      breathe.stop();
    };
  }, [rotation, pulse]);

  const rotate = rotation.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });
  const scale = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.08] });
  const opacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.85, 1] });

  return (
    <View
      pointerEvents="none"
      style={[StyleSheet.absoluteFillObject, { alignItems: 'center', justifyContent: 'center', backgroundColor }]}
    >
      {/* Soft blurred backdrop using the poster thumbnail (when available)
          gives the loader visual context — visitors see a hint of the
          asset that's coming rather than a blank panel. */}
      {posterUrl ? (
        <Image
          source={{ uri: posterUrl }}
          style={[
            StyleSheet.absoluteFillObject,
            isWeb ? { filter: 'blur(28px) saturate(1.1) brightness(0.55)' } : { opacity: 0.25 },
          ]}
          resizeMode="cover"
          blurRadius={isWeb ? 0 : 24}
        />
      ) : null}
      {/* Vignette / dim wash so the white text reads cleanly against any
          backdrop (poster or solid color). */}
      <View
        style={[
          StyleSheet.absoluteFillObject,
          { backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.55)' },
        ]}
      />

      {/* Animated cube. Stacks rotation (transform[0]) + breathing scale
          (transform[1]) so they multiply rather than overwrite each other. */}
      <Animated.View
        style={{
          transform: [{ rotate }, { scale }],
          opacity,
          marginBottom: 18,
        }}
      >
        <Icon name="cube" size={42} color={isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)'} />
      </Animated.View>

      <Text
        size="m"
        weight="semiBold"
        style={{
          color: isDark ? '#FFFFFF' : '#0A0A0A',
          marginBottom: 6,
          letterSpacing: -0.1,
        }}
      >
        Rendering 3D scene
      </Text>
      <Text
        size="xs"
        style={{
          color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)',
          textAlign: 'center',
          maxWidth: 280,
          lineHeight: 18,
          paddingHorizontal: 16,
        }}
      >
        {Platform.OS === 'macos'
          ? 'Loading your model. Larger files may take a moment.'
          : 'Hang tight — large meshes can take a few seconds to download and render in your browser.'}
      </Text>
    </View>
  );
});
LoadingOverlay.displayName = 'Model3DLoadingOverlay';

function Model3DViewer({
  modelUrl,
  posterUrl,
  iosSrc,
  alternateFormats,
  autoRotate = true,
  style,
  onError,
}) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const [hasError, setHasError] = useState(false);
  // Lets the user dismiss the "Could not load 3D model" overlay so they're
  // left with just the thumbnail visible (no error blocking the asset).
  const [errorDismissed, setErrorDismissed] = useState(false);
  // Tracks when the model has finished downloading + rendering its first
  // frame. Drives the loading overlay so we can hold a friendly "rendering
  // 3D scene" message until the GLB / OBJ is actually viewable.
  const [isModelReady, setIsModelReady] = useState(false);

  const inferredFormat = useMemo(() => getInferredFormat(modelUrl), [modelUrl]);

  // If the primary asset isn't a viewer-supported format, prefer a glb/gltf
  // alternate when one is available (Meshy/Tripo often return obj/fbx as primary).
  // We *only* swap when the primary itself isn't something we can render —
  // model-viewer-supported (glb/gltf) or three-supported (obj/stl/ply).
  const effectiveModelUrl = useMemo(() => {
    if (!modelUrl) return null;
    if (!inferredFormat || SUPPORTED_FORMATS.has(inferredFormat)) return modelUrl;
    const glbAlt = alternateFormats?.find((f) => SUPPORTED_BY_MODEL_VIEWER.has((f.format || '').toLowerCase()));
    if (glbAlt?.url) return glbAlt.url;
    const threeAlt = alternateFormats?.find((f) => SUPPORTED_BY_THREE.has((f.format || '').toLowerCase()));
    return threeAlt?.url || modelUrl;
  }, [modelUrl, alternateFormats, inferredFormat]);

  const effectiveFormat = useMemo(() => getInferredFormat(effectiveModelUrl), [effectiveModelUrl]);

  const useThreeRenderer = !!effectiveFormat && SUPPORTED_BY_THREE.has(effectiveFormat);
  const useModelViewer = !!effectiveFormat && SUPPORTED_BY_MODEL_VIEWER.has(effectiveFormat);
  const isFormatViewable = useModelViewer || useThreeRenderer || !effectiveFormat;

  const mtlUrl = useMemo(() => (useThreeRenderer && effectiveFormat === 'obj' ? findMtlUrl(alternateFormats) : null), [useThreeRenderer, effectiveFormat, alternateFormats]);
  const textureUrl = useMemo(() => (useThreeRenderer && effectiveFormat === 'obj' ? findTextureUrl(alternateFormats) : null), [useThreeRenderer, effectiveFormat, alternateFormats]);

  const backgroundColor = colors.solid_color_secondary;

  // A regenerated alternate or texture is a fresh preview even when the
  // unsupported primary URL remains the same. Each frame loads its own modules.
  useEffect(() => {
    setHasError(false);
    setErrorDismissed(false);
    setIsModelReady(false);
  }, [effectiveModelUrl, mtlUrl, textureUrl]);

  if (!effectiveModelUrl) {
    return (
      <View style={[styles.placeholder, { backgroundColor }, style]}>
        <Icon name="cube" size={28} color={colors.text_color_tertiary} />
        <Text size="xs" color="tertiary" style={styles.placeholderText}>No 3D model</Text>
      </View>
    );
  }

  // Platform doesn't ship a viable renderer (e.g. RN-windows, tvOS, an env
  // without react-native-webview). Show the same "download to view" placeholder
  // so hosts that bypass `isModel3DRenderable` still degrade gracefully.
  if (!isPlatformSupported || (!isWeb && !isWebViewAvailable())) {
    return (
      <View style={[styles.placeholder, { backgroundColor }, style]}>
        <Icon name="cube" size={28} color={colors.text_color_tertiary} />
        <Text size="xs" color="tertiary" weight="medium" style={styles.placeholderText}>
          Preview unavailable
        </Text>
        <Text size="xxs" color="tertiary" style={{ marginTop: 4 }}>
          {effectiveFormat ? `.${effectiveFormat.toUpperCase()} format` : '3D model'} - download to view
        </Text>
      </View>
    );
  }

  if (!isFormatViewable) {
    return (
      <View style={[styles.placeholder, { backgroundColor }, style]}>
        <Icon name="cube" size={28} color={colors.text_color_tertiary} />
        <Text size="xs" color="tertiary" weight="medium" style={styles.placeholderText}>
          Preview unavailable
        </Text>
        <Text size="xxs" color="tertiary" style={{ marginTop: 4 }}>
          {effectiveFormat ? `.${effectiveFormat.toUpperCase()} format` : 'Unknown format'} - download to view
        </Text>
      </View>
    );
  }

  if (hasError) {
    return (
      <View style={[styles.container, { backgroundColor }, style]}>
        {/* Thumbnail as background — gives the user something useful to look
            at instead of a flat error panel. Falls back to a plain colored
            surface if no poster was provided. */}
        {posterUrl ? (
          <Image
            source={{ uri: posterUrl }}
            style={styles.errorThumbnail}
            resizeMode="contain"
          />
        ) : (
          <View style={[styles.errorThumbnail, { backgroundColor }]} />
        )}
        {/* Opaque white overlay with the error message + dismiss CTA. Once
            dismissed, the thumbnail shows through cleanly. */}
        {!errorDismissed && (
          <View style={styles.errorOverlay} pointerEvents="box-none">
            <View style={styles.errorOverlayInner}>
              <Icon name="triangle-exclamation" size={28} color="#000" />
              <Text size="sm" weight="medium" style={styles.errorOverlayText}>
                Could not load 3D model
              </Text>
              <TouchableOpacity
                onPress={() => { setHasError(false); setErrorDismissed(false); setIsModelReady(false); }}
                style={styles.errorOverlayButton}
                accessibilityRole="button"
                accessibilityLabel="Retry 3D preview"
              >
                <Text size="sm" weight="semibold" style={styles.errorOverlayButtonText}>Retry</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setErrorDismissed(true)}
                style={styles.errorOverlayButton}
                accessibilityRole="button"
                accessibilityLabel="Dismiss"
              >
                <Text size="sm" weight="semibold" style={styles.errorOverlayButtonText}>
                  Dismiss
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    );
  }

  // ─── three.js path (obj / stl / ply) ─────────────────────────────────
  // Both web and native funnel into the same self-contained HTML — web mounts
  // it via an <iframe srcDoc>, native via the existing react-native-webview.
  if (useThreeRenderer) {
    return (
      <View style={[styles.container, { backgroundColor }, style]}>
        <ThreeViewerHost
          modelUrl={effectiveModelUrl}
          mtlUrl={mtlUrl}
          textureUrl={textureUrl}
          format={effectiveFormat}
          autoRotate={autoRotate}
          backgroundColor={backgroundColor}
          style={StyleSheet.absoluteFillObject}
          colors={colors}
          onError={() => { setHasError(true); onError?.(); }}
          onReady={() => setIsModelReady(true)}
        />
        {!isModelReady && (
          <LoadingOverlay
            posterUrl={posterUrl}
            backgroundColor={backgroundColor}
            colors={colors}
            isDark={isDark}
          />
        )}
      </View>
    );
  }

  // ─── model-viewer path (glb / gltf) ──────────────────────────────────
  // Web hosts this in a srcDoc iframe. RN-web's custom-element wrapper
  // leaves <model-viewer> at the default 300×150 host and never fires
  // `load`, which stuck Work GLBs behind "Rendering 3D scene".
  if (isWeb) {
    return (
      <View style={[styles.container, { backgroundColor }, style]}>
        <ModelViewerHost
          modelUrl={effectiveModelUrl}
          posterUrl={posterUrl}
          iosSrc={iosSrc}
          autoRotate={autoRotate}
          backgroundColor={backgroundColor}
          style={StyleSheet.absoluteFillObject}
          colors={colors}
          onError={() => { setHasError(true); onError?.(); }}
          onReady={() => setIsModelReady(true)}
        />
        {!isModelReady && (
          <LoadingOverlay
            posterUrl={posterUrl}
            backgroundColor={backgroundColor}
            colors={colors}
            isDark={isDark}
          />
        )}
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor }, style]}>
      <NativeWebViewer
        modelUrl={effectiveModelUrl}
        posterUrl={posterUrl}
        iosSrc={iosSrc}
        autoRotate={autoRotate}
        backgroundColor={backgroundColor}
        style={StyleSheet.absoluteFillObject}
        colors={colors}
        onError={() => { setHasError(true); onError?.(); }}
        onReady={() => setIsModelReady(true)}
      />
      {!isModelReady && (
        <LoadingOverlay
          posterUrl={posterUrl}
          backgroundColor={backgroundColor}
          colors={colors}
          isDark={isDark}
        />
      )}
    </View>
  );
}

function ModelViewerHost({ modelUrl, posterUrl, iosSrc, autoRotate, backgroundColor, style, colors, onError, onReady }) {
  const html = useMemo(
    () => buildViewerHtml({ modelUrl, posterUrl, iosSrc, autoRotate, backgroundColor }),
    [modelUrl, posterUrl, iosSrc, autoRotate, backgroundColor],
  );
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!isWeb) return undefined;
    const handler = (e) => {
      if (!e?.data || typeof e.data !== 'string') return;
      let parsed;
      try { parsed = JSON.parse(e.data); } catch (_) { return; }
      if (parsed?.source !== 'gab-3d-viewer') return;
      if (iframeRef.current && e.source && e.source !== iframeRef.current.contentWindow) return;
      if (parsed.type === 'error') onError?.();
      else if (parsed.type === 'ready') onReady?.();
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onError, onReady]);

  if (isWeb) {
    return (
      <View style={[styles.container, { backgroundColor }, style]}>
        {React.createElement('iframe', {
          ref: iframeRef,
          srcDoc: html,
          title: '3D model viewer',
          allow: 'fullscreen; xr-spatial-tracking',
          style: {
            width: '100%',
            height: '100%',
            border: '0',
            backgroundColor,
            display: 'block',
          },
        })}
      </View>
    );
  }

  return (
    <NativeWebViewer
      html={html}
      backgroundColor={backgroundColor}
      style={style}
      colors={colors}
      onError={onError}
      onReady={onReady}
    />
  );
}

function ThreeViewerHost({ modelUrl, mtlUrl, textureUrl, format, autoRotate, backgroundColor, style, colors, onError, onReady }) {
  const html = useMemo(
    () => buildThreeViewerHtml({ modelUrl, mtlUrl, textureUrl, format, autoRotate, backgroundColor }),
    [modelUrl, mtlUrl, textureUrl, format, autoRotate, backgroundColor],
  );
  const iframeRef = useRef(null);

  // Web: listen for postMessage('error' | 'ready') from inside the srcDoc
  // iframe so we can flip the parent card back to the thumbnail on error
  // and dismiss the loading overlay when the first frame draws.
  useEffect(() => {
    if (!isWeb) return;
    const handler = (e) => {
      if (!e?.data || typeof e.data !== 'string') return;
      let parsed;
      try { parsed = JSON.parse(e.data); } catch (_) { return; }
      if (parsed?.source !== 'gab-3d-viewer') return;
      // Only react to messages coming from *our* iframe instance (defends
      // against multiple viewers on the page racing each other).
      if (iframeRef.current && e.source && e.source !== iframeRef.current.contentWindow) return;
      if (parsed.type === 'error') onError?.();
      else if (parsed.type === 'ready') onReady?.();
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onError, onReady]);

  if (isWeb) {
    return (
      <View style={[styles.container, { backgroundColor }, style]}>
        {React.createElement('iframe', {
          ref: iframeRef,
          srcDoc: html,
          title: '3D model viewer',
          allow: 'fullscreen; xr-spatial-tracking',
          style: {
            width: '100%',
            height: '100%',
            border: '0',
            backgroundColor,
            display: 'block',
          },
          // Intentionally no onError on the iframe element — for srcDoc
          // iframes the browser-level error event is unreliable (some
          // engines fire it on benign content negotiation paths). The
          // authoritative signal is the postMessage('error') sent from
          // inside the iframe's load() catch block.
        })}
      </View>
    );
  }

  return (
    <NativeWebViewer
      html={html}
      backgroundColor={backgroundColor}
      style={style}
      colors={colors}
      onError={onError}
      onReady={onReady}
    />
  );
}

function NativeWebViewer({ html: htmlProp, modelUrl, posterUrl, iosSrc, autoRotate, backgroundColor, style, colors, onError, onReady }) {
  // Resolve once per mount so a missing WebView module on an unsupported
  // platform (e.g. RN-windows, certain RN-macOS builds) degrades to the
  // "Preview unavailable" placeholder instead of throwing on require.
  const WebView = resolveWebView();
  // When `html` is supplied (three.js path) we render it as-is. Otherwise fall
  // back to the model-viewer HTML the original signature expected.
  const html = useMemo(
    () => (htmlProp || buildViewerHtml({ modelUrl, posterUrl, iosSrc, autoRotate, backgroundColor })),
    [htmlProp, modelUrl, posterUrl, iosSrc, autoRotate, backgroundColor],
  );

  // Bridge in-page error / ready messages from the three.js loader (or
  // model-viewer poster failures) to the host so it can swap back to the
  // thumbnail on error and dismiss the loading overlay on ready.
  const handleMessage = useCallback((event) => {
    const raw = event?.nativeEvent?.data;
    if (!raw || typeof raw !== 'string') return;
    let parsed;
    try { parsed = JSON.parse(raw); } catch (_) { return; }
    if (parsed?.source !== 'gab-3d-viewer') return;
    if (parsed.type === 'error') onError?.();
    else if (parsed.type === 'ready') onReady?.();
  }, [onError, onReady]);

  // No WebView available on this platform — surface the same placeholder the
  // top-level component uses, so the host's onError fallback can flip back to
  // the thumbnail without us crashing on a null component.
  if (!WebView) {
    if (onError) {
      // Fire on the next tick so we don't trigger a setState during render
      // for the parent.
      setTimeout(() => onError(), 0);
    }
    return (
      <View style={[styles.placeholder, { backgroundColor }, style]}>
        <Icon name="cube" size={28} color={colors.text_color_tertiary} />
        <Text size="xs" color="tertiary" weight="medium" style={styles.placeholderText}>
          Preview unavailable
        </Text>
        <Text size="xxs" color="tertiary" style={{ marginTop: 4 }}>
          Download to view
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor }, style]}>
      <WebView
        // macOS keeps the CDN-approved app origin across beta/API changes;
        // renderer URLs are also HTTPS to avoid mixed-content failures.
        source={{ html, baseUrl: VIEWER_BASE_URL }}
        style={[styles.webview, { backgroundColor }]}
        originWhitelist={['*']}
        javaScriptEnabled
        domStorageEnabled
        allowsInlineMediaPlayback
        mediaPlaybackRequiresUserAction={false}
        scrollEnabled={false}
        scalesPageToFit={false}
        bounces={false}
        androidLayerType="hardware"
        onMessage={handleMessage}
        onError={onError}
        onHttpError={onError}
        renderLoading={() => (
          <View style={[styles.loadingOverlay, { backgroundColor }]}>
            <ActivityIndicator size="small" color={colors.text_color_secondary} />
          </View>
        )}
        startInLoadingState
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  webview: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  placeholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
  },
  placeholderText: {
    marginTop: 8,
    textAlign: 'center',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorThumbnail: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  errorOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    // Opaque white so the (broken) viewer area underneath is fully hidden
    // until the user dismisses, then the thumbnail behind reveals.
    backgroundColor: 'rgba(255, 255, 255, 0.96)',
  },
  errorOverlayInner: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 20,
    maxWidth: 320,
  },
  errorOverlayText: {
    marginTop: 12,
    color: '#000',
    textAlign: 'center',
  },
  errorOverlayButton: {
    marginTop: 16,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: '#000',
  },
  errorOverlayButtonText: {
    color: '#fff',
  },
});

Model3DViewer.propTypes = {
  modelUrl: PropTypes.string,
  posterUrl: PropTypes.string,
  iosSrc: PropTypes.string,
  alternateFormats: PropTypes.arrayOf(PropTypes.shape({
    format: PropTypes.string,
    url: PropTypes.string,
  })),
  autoRotate: PropTypes.bool,
  style: PropTypes.any,
  onError: PropTypes.func,
};

export default React.memo(Model3DViewer);
